import { useState, useEffect, useCallback, useRef } from "react";
import Fuse from "fuse.js";
import { ChatEntity } from "../types";
import { getChatsWithoutHubIdFromLocalDB } from "../services/chats";
import { isUserAuthenticated } from "@/middleware/auth";
import { useToast } from "@/hooks/useToast";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 20;

const sortByRecency = (a: ChatEntity, b: ChatEntity): number => {
  const dateA = a.lastMessageTimestamp
    ? new Date(a.lastMessageTimestamp).getTime()
    : null;
  const dateB = b.lastMessageTimestamp
    ? new Date(b.lastMessageTimestamp).getTime()
    : null;

  if (dateA === null && dateB === null) {
    return 0;
  }
  if (dateA === null) {
    return 1;
  }
  if (dateB === null) {
    return -1;
  }
  return dateB - dateA;
};

export function useForwardChatList(
  searchTerm: string,
  isOpen: boolean,
  excludeChatId?: string,
) {
  const { toast } = useToast();
  const { t } = useTranslation();

  const [allChats, setAllChats] = useState<ChatEntity[]>([]);
  const [results, setResults] = useState<ChatEntity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Track page size across renders without triggering effects
  const pageEndRef = useRef(PAGE_SIZE);

  // Reload chats each time the modal opens so we always have fresh data
  // and don't hit the auth-not-ready problem on first mount.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const load = async () => {
      const userFingerPrint = isUserAuthenticated();
      if (!userFingerPrint) {
        return;
      }

      setIsLoading(true);
      pageEndRef.current = PAGE_SIZE;

      try {
        const chats = await getChatsWithoutHubIdFromLocalDB();
        setAllChats(chats);
      } catch (error) {
        toast({
          duration: 1500,
          description:
            error instanceof Error ? error.message : t("cannotLoadChats"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Derive the visible page from allChats + searchTerm
  const getFiltered = useCallback(
    (chats: ChatEntity[]): ChatEntity[] => {
      // Exclude the source chat so the user can't forward to the same chat
      const eligible = excludeChatId
        ? chats.filter((c) => c.id !== excludeChatId)
        : chats;

      const trimmed = searchTerm.trim();

      if (!trimmed) {
        return [...eligible].sort(sortByRecency);
      }

      const fuse = new Fuse(eligible, {
        keys: ["name"],
        includeScore: true,
        threshold: 0.4,
        minMatchCharLength: 1,
      });

      return fuse
        .search(trimmed)
        .map((r) => r.item)
        .sort(sortByRecency);
    },
    [searchTerm, excludeChatId],
  );

  // Re-slice whenever allChats or searchTerm changes; reset to first page.
  useEffect(() => {
    pageEndRef.current = PAGE_SIZE;
    const filtered = getFiltered(allChats);
    setResults(filtered.slice(0, PAGE_SIZE));
    setHasMore(filtered.length > PAGE_SIZE);
  }, [allChats, searchTerm, getFiltered]);

  const loadMore = useCallback(() => {
    const newEnd = pageEndRef.current + PAGE_SIZE;
    pageEndRef.current = newEnd;

    const filtered = getFiltered(allChats);
    setResults(filtered.slice(0, newEnd));
    setHasMore(filtered.length > newEnd);
  }, [allChats, getFiltered]);

  return { results, isLoading, hasMore, loadMore };
}
